import React, { Fragment, ReactChild, SetStateAction } from 'react';
import { List, ListItem } from './TableList.style';
import { useHover } from '../../hooks/useHover';
import { ListChildComponentProps } from 'react-window';
import { Checkbox, TristateCheckbox } from '@components/form';
import VirtualList, { ItemInfo } from '@components/react-tiny-virtual-list';
import useDebouncedResizeObserver from '@hooks/useDebouncedResizeObserver';
import { Button } from '@components/button';
import { FlexCol } from '@components/layout/Flex';
import { useI18nContext } from '@i18n/i18n-react';
import { ThreeDots } from '@components/icons/FarseerIcons';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type BaseTableListRow = { [key: string]: any };

interface State {
    hovered: boolean;
}

export interface TableListColumn<TTableListRow extends BaseTableListRow> {
    title?: string;
    width?: string;
    getValue(row: TTableListRow, state: State): ReactChild | null | undefined;
}

export interface TableListProps<TTableListRow extends BaseTableListRow, TSelectable extends boolean = false> {
    selectable?: TSelectable;
    selected?: TSelectable extends true ? TTableListRow[] : never;
    onSelect?: TSelectable extends true ? (selected: TTableListRow[]) => void : never;

    rows: TTableListRow[];
    columns: TableListColumn<TTableListRow>[];
    gap?: number;
    itemHeight?: number;
    justifyContent?: 'space-between' | 'center' | 'left';
    hideColumns?: boolean;
    keyExtractor(row: TTableListRow): string;
    loadMore?: React.Dispatch<SetStateAction<void>>;
    scrollable?: boolean;
}

interface TableListItemProps<TTableListRow extends BaseTableListRow> {
    row: TTableListRow;
    rowKey: string;
    columns: TableListColumn<TTableListRow>[];
    gap: number;
    gridTemplateColumns: string;
    justifyContent: 'space-between' | 'center' | 'left';
    select: JSX.Element | null;
    checked: boolean;
}

function TableListItem<TTableListRow extends BaseTableListRow>(
    props: ListChildComponentProps<TableListItemProps<TTableListRow>[]>
) {
    const { data, index, style } = props;
    const item = data[index];
    const { row, rowKey, columns, gap, gridTemplateColumns, justifyContent, select, checked } = item;
    const [hovered, handlers] = useHover();

    const rowItems = columns.map((c, i) => {
        const value = c.getValue(row, { hovered });
        const key = c.title ?? i;
        return typeof value === 'string' || typeof value === 'number' ? (
            <span key={key}>{value}</span>
        ) : (
            <Fragment key={key}>{value}</Fragment>
        );
    });

    if (select) {
        rowItems.unshift(select);
    }

    return (
        <ListItem
            key={rowKey}
            $selected={checked}
            style={{ ...style, justifyContent, gridTemplateColumns, gap }}
            {...handlers}
        >
            {rowItems}
        </ListItem>
    );
}

export function TableList<TTableListRow extends BaseTableListRow, TSelectable extends boolean = false>(
    props: TableListProps<TTableListRow, TSelectable>
) {
    const {
        selectable,
        selected,
        onSelect,
        rows,
        columns,
        hideColumns = false,
        gap = 8,
        itemHeight = 32,
        justifyContent = 'left',
        keyExtractor,
        loadMore,
        scrollable = false,
    } = props;

    const { LL } = useI18nContext();
    const columnsTemplate = columns.map((c) => c.width ?? '1fr').join(' ');
    const gridTemplateColumns = selectable ? '32px ' + columnsTemplate : columnsTemplate;
    const selectedByKey = new Map(selected?.map((s) => [keyExtractor(s), s]));
    const rowsByKey = new Map(rows.map((r) => [keyExtractor(r), r]));

    const { ref, height } = useDebouncedResizeObserver(500, true);

    const columnHeaders = columns.map((c, i) => {
        const key = c.title ?? i;
        return <span key={key}>{c.title}</span>;
    });
    if (selectable) {
        let allSelected = rowsByKey.size > 0;
        for (const rowKey of rowsByKey.keys()) {
            if (!selectedByKey.has(rowKey)) {
                allSelected = false;
                break;
            }
        }
        const value = allSelected || (selected?.length ? null : false);
        columnHeaders.unshift(
            <TristateCheckbox
                key="select-all"
                value={value}
                onChange={(allSelected) => {
                    onSelect?.(allSelected ? rows : []);
                }}
            />
        );
    }

    const columnsHeader = hideColumns ? null : (
        <ListItem
            $header
            style={{ justifyContent, gridTemplateColumns, gap, position: 'relative', top: 0, left: 0, right: 0 }}
        >
            {columnHeaders}
        </ListItem>
    );

    const listItems: TableListItemProps<TTableListRow>[] = [];
    for (const [rowKey, row] of rowsByKey) {
        const checked = selectedByKey.has(rowKey);
        listItems.push({
            gap,
            row,
            rowKey,
            columns,
            gridTemplateColumns,
            justifyContent,
            checked,
            select: selectable ? (
                <Checkbox
                    key={rowKey + '-select'}
                    value={checked}
                    onChange={() => {
                        if (!checked) {
                            onSelect?.(selected ? [...selected, row] : [row]);
                            return;
                        }

                        const updated: TTableListRow[] = [];
                        for (const [key, item] of selectedByKey) {
                            if (key === rowKey) {
                                continue;
                            }

                            updated.push(item);
                        }

                        onSelect?.(updated);
                    }}
                />
            ) : null,
        });
    }

    const renderItem = ({ index, style }: ItemInfo) => (
        <TableListItem key={listItems[index].rowKey} index={index} style={style} data={listItems} />
    );

    return (
        <List>
            {columnsHeader}
            <FlexCol ref={ref} $flexGrow style={{ contain: 'layout' }}>
                <VirtualList
                    height={scrollable ? height : itemHeight * listItems.length}
                    width={'100%'}
                    itemCount={listItems.length}
                    itemSize={itemHeight}
                    renderItem={renderItem}
                />
            </FlexCol>
            {loadMore ? (
                <div>
                    <Button
                        icon={ThreeDots}
                        $type="quaternary"
                        title={LL.LOAD_MORE()}
                        onClick={() => loadMore()}
                        style={{ marginTop: '10px' }}
                    />
                </div>
            ) : null}
        </List>
    );
}
